import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { acceptInvitation } from '@/services/invitationService';
import { useToast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';
import { EmailMismatchDialog } from '@/components/invitation/EmailMismatchDialog';

export default function InviteRedirect() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(true);
  const [showMismatchDialog, setShowMismatchDialog] = useState(false);
  const [mismatchEmails, setMismatchEmails] = useState<{
    invited: string;
    current: string;
  } | null>(null);

  // Handler for automatic account switching
  const handleSwitchAccount = async () => {
    setShowMismatchDialog(false);
    setIsProcessing(true);
    
    try {
      console.log('Switching account - logging out current user');
      
      // Sign out current user
      await supabase.auth.signOut();
      
      // Clear local storage
      localStorage.clear();
      
      // Navigate to auth with invite token and invited email
      const params = new URLSearchParams();
      params.set('invite', token!);
      params.set('email', mismatchEmails!.invited);
      params.set('switched_account', 'true');
      
      // Use window.location for full reload
      window.location.href = `/auth?${params.toString()}`;
    } catch (error) {
      console.error('Failed to switch account:', error);
      toast({
        title: 'Error',
        description: 'Failed to log out. Please try again.',
        variant: 'destructive',
      });
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    const processInvite = async () => {
      if (!token) {
        toast({
          title: 'Invalid invite link',
          description: 'The invitation link is invalid.',
          variant: 'destructive',
        });
        navigate('/');
        return;
      }

      try {
        // Check if user is logged in
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // User is logged in - first validate email matches via edge function
          try {
            const { data, error } = await supabase.functions.invoke('accept-invite-auto-signup', {
              body: { 
                inviteToken: token,
                currentUserEmail: session.user.email // Pass for validation
              }
            });

            if (error) throw error;

            if (data?.error === 'email_mismatch') {
              // Email mismatch - show dialog with automatic switch option
              console.log('Email mismatch detected:', {
                invited: data.invitedEmail,
                current: session.user.email
              });
              
              setMismatchEmails({
                invited: data.invitedEmail,
                current: session.user.email || ''
              });
              setShowMismatchDialog(true);
              setIsProcessing(false);
              return;
            }

            if (data?.success) {
              toast({
                title: 'Invitation accepted',
                description: 'You have successfully joined the company.',
              });
              navigate('/companies');
              return;
            }
          } catch (edgeFnError: any) {
            console.error('Edge function validation failed:', edgeFnError);
            // Fallback to service function which also has validation
            try {
              const result = await acceptInvitation(token);
              
              if (result.alreadyMember) {
                toast({
                  title: 'Already a member',
                  description: 'You are already a member of this company.',
                });
              } else {
                toast({
                  title: 'Invitation accepted',
                  description: 'You have successfully joined the company.',
                });
              }
              navigate('/companies');
            } catch (serviceError: any) {
              throw serviceError; // Will be caught by outer catch
            }
          }
        } else {
          // User not logged in, try auto-signup
          const email = searchParams.get('email');
          
          try {
            const { data, error } = await supabase.functions.invoke('accept-invite-auto-signup', {
              body: { inviteToken: token }
            });

            if (error) throw error;

            if (data?.requiresLogin) {
              // User already exists, redirect to login
              const params = new URLSearchParams();
              params.set('invite', token);
              if (email) {
                params.set('email', email);
              }
              navigate(`/auth?${params.toString()}`);
            } else if (data?.success) {
              // Account created successfully
              const message = data.passwordEmailSent
                ? 'Check your email to set your password.'
                : data.message || 'You can set your password from the login page.';
              
              toast({
                title: 'Account created!',
                description: message,
              });
              const params = new URLSearchParams();
              params.set('account_created', 'true');
              params.set('email', data.email);
              params.set('password_email_sent', data.passwordEmailSent ? 'true' : 'false');
              navigate(`/auth?${params.toString()}`);
            }
          } catch (autoSignupError: any) {
            console.error('Auto-signup failed:', autoSignupError);
            // Fallback to manual signup flow
            const params = new URLSearchParams();
            params.set('invite', token);
            if (email) {
              params.set('email', email);
            }
            navigate(`/auth?${params.toString()}`);
          }
        }
      } catch (error: any) {
        console.error('Failed to process invitation:', error);
        toast({
          title: 'Failed to accept invitation',
          description: error.message || 'The invitation may be invalid or expired.',
          variant: 'destructive',
        });
        navigate('/');
      } finally {
        setIsProcessing(false);
      }
    };

    processInvite();
  }, [token, searchParams, navigate, toast]);

  return (
    <>
      <EmailMismatchDialog
        open={showMismatchDialog}
        invitedEmail={mismatchEmails?.invited || ''}
        currentEmail={mismatchEmails?.current || ''}
        onConfirmSwitch={handleSwitchAccount}
        onCancel={() => {
          setShowMismatchDialog(false);
          navigate('/');
        }}
      />
      
      {isProcessing && (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Processing your invitation...</p>
          </div>
        </div>
      )}
    </>
  );
}
